import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  PaperIconComponent,
  PaperButtonComponent,
} from '@ricoh-paper/angular';
import { PaperRichTextComponent } from '@ricoh-paper/angular/rich-text';
import {
  FlowProject,
  FlowProjectKnowledgePage,
  FlowProjectMeeting,
  FlowProjectsService,
} from '../../../../core/services/flow-projects.service';
import { MeetingPanelService } from '../../../../core/services/meeting-panel.service';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { WorkspaceStateService } from '../../../../core/services/workspace-state.service';
import { stripHtml } from '../../../../core/utils/text';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import { PaperConfirmDialogService } from '../../../../shared/services/paper-confirm-dialog.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { withClock } from '../../../../core/utils/format-prefs';

/**
 * Project detail panel — right-anchored slide-in mirroring the
 * people-panel chrome (backdrop + 540px panel + 350ms slide).
 *
 * Surface contents:
 *   - Header: project name + icon, archive toggle, close.
 *   - Description block (inline-edit click to focus).
 *   - Members list (read-only in v1; edit affordance lands when
 *     the cross-viewer sharing surface ships).
 *   - Meetings list — every UserCalendarMeeting tagged into the
 *     project, with click-through to the meeting panel.
 *
 * Mounted once globally inside app-shell.component.html so it's
 * reachable from any page via ShellStateService.openProjectDetail().
 *
 * The component does NOT own a copy of the project — it reads from
 * FlowProjectsService.projectById() which is the single source of
 * truth. Inline edits mutate via the service; the chip cluster on
 * the meeting panel + this surface stay in sync.
 */
@Component({
  selector: 'im-project-detail-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PaperIconComponent,
    PaperButtonComponent,
    PaperRichTextComponent,
    FocusTrapDirective,
    TranslocoPipe,
  ],
  templateUrl: './project-detail-panel.component.html',
  styleUrl: './project-detail-panel.component.scss',
  /* Its buttons and links are labelled with masked Flow content, and
     posthog-js folds a nested span's text into the button's own, where the
     mask cannot reach it. So nothing here autocaptures (tv45.100). */
  host: { class: 'ph-no-capture' },
})
export class ProjectDetailPanelComponent {
  protected readonly shellState = inject(ShellStateService);
  protected readonly flowProjects = inject(FlowProjectsService);
  protected readonly workspaceState = inject(WorkspaceStateService);
  private readonly meetingPanel = inject(MeetingPanelService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly confirmDialog = inject(PaperConfirmDialogService);

  /** Currently-viewing project ID (null = panel closed). */
  protected readonly projectId = this.shellState.projectDetailId;

  /** Resolved project from the cache. May be null briefly after the
   *  panel opens but before `load()` resolves; we guard the template
   *  on truthiness throughout. */
  protected readonly project = computed<FlowProject | null>(() => {
    const id = this.projectId();
    if (!id) return null;
    return this.flowProjects.projectById().get(id) ?? null;
  });

  // ── Inline-edit state ──────────────────────────────

  protected readonly editingName = signal(false);
  protected readonly editingDescription = signal(false);
  protected readonly nameDraft = signal('');
  protected readonly descriptionDraft = signal('');

  // ── Attached meetings ──────────────────────────────

  /** Rich UCM rows attached to the project. Loaded lazily on open
   *  via flowProjectMeetings. Each row carries subject + start time
   *  + recording badge + isAutoAttached flag. */
  protected readonly meetings = signal<FlowProjectMeeting[]>([]);
  protected readonly meetingsLoading = signal(false);

  // ── Knowledge integration ────────────────────────────
  //
  // The Knowledge section sits between Description and Members.
  // Two states: "Linked" (workspace + folder + page list) and
  // "Empty" (CTA to link a workspace, opens an inline picker).

  /** Knowledge pages inside the project's folder. Loaded lazily on
   *  open via fetchKnowledgePages — empty when the project isn't
   *  linked to a workspace. */
  protected readonly knowledgePages = signal<FlowProjectKnowledgePage[]>([]);
  protected readonly knowledgePagesLoading = signal(false);

  /** Inline picker state — null when closed, the project ID when
   *  opened. Single picker per panel; closing on link or cancel. */
  protected readonly workspacePickerOpen = signal(false);
  protected readonly workspacePickerQuery = signal('');

  /** Picker suggestions: workspaces the viewer is a member of, with
   *  the project's currently-linked workspace (if any) filtered out. */
  protected readonly workspacePickerSuggestions = computed(() => {
    const q = this.workspacePickerQuery().trim().toLowerCase();
    const linkedId = this.project()?.knowledgeWorkspaceId;
    const candidates = this.workspaceState
      .workspaces()
      .filter(w => w._id !== linkedId);
    if (!q) return candidates;
    return candidates.filter(w => w.name.toLowerCase().includes(q));
  });

  /** Recent post-link success state — drives the chip pulse + the
   *  inline "✓ Linked to <name>" affordance for ~1.5s before the
   *  panel re-renders to its linked state. */
  protected readonly knowledgeJustLinkedName = signal<string | null>(null);
  private knowledgeLinkedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Re-load attached meetings + knowledge pages whenever the panel
    // opens on a different project.
    effect(() => {
      const id = this.projectId();
      if (!id) {
        this.meetings.set([]);
        this.knowledgePages.set([]);
        this.closeWorkspacePicker();
        return;
      }
      // Ensure the FlowProjectsService cache is warm so project()
      // resolves. Fire and forget — the template guards on null.
      void this.flowProjects.load();
      void this.workspaceState.loadWorkspaces();
      void this.loadMeetings(id);
      void this.loadKnowledgePages(id);
    });
  }

  // ── Keyboard ───────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.projectId()) return;
    if (this.editingName()) {
      this.cancelNameEdit();
      return;
    }
    if (this.editingDescription()) {
      this.cancelDescriptionEdit();
      return;
    }
    this.shellState.closeProjectDetail();
  }

  // ── Name edit ──────────────────────────────────────

  protected beginNameEdit(): void {
    const project = this.project();
    if (!project) return;
    this.nameDraft.set(project.name);
    this.editingName.set(true);
  }

  protected cancelNameEdit(): void {
    this.editingName.set(false);
    this.nameDraft.set('');
  }

  protected async commitNameEdit(): Promise<void> {
    const id = this.projectId();
    const value = this.nameDraft().trim();
    if (!id || !value) {
      this.cancelNameEdit();
      return;
    }
    const project = this.project();
    if (project && value === project.name) {
      this.cancelNameEdit();
      return;
    }
    await this.flowProjects.updateProject(id, { name: value });
    this.cancelNameEdit();
  }

  // ── Description edit ───────────────────────────────
  //
  // The description field is rich-text via TipTap. Stored as sanitised
  // HTML on the server (StarterKit schema enforces allowed nodes/marks).
  // Read mode renders via DomSanitizer.bypassSecurityTrustHtml since the
  // server-stored value has already been schema-filtered on every save.

  /** Memoised sanitised description for read mode. Recomputes whenever
   *  the underlying project signal changes (which happens after a
   *  successful update, since FlowProjectsService.updateProject swaps
   *  the cache entry). */
  protected readonly safeDescription = computed<SafeHtml>(() => {
    const html = this.project()?.description ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  /** True when the description has any visible text (after stripping
   *  HTML). The TipTap editor emits `<p></p>` when empty even after we
   *  normalise it; this catches lingering empty markup so the empty
   *  state still surfaces. */
  protected readonly hasDescription = computed(() => {
    const html = this.project()?.description ?? '';
    return stripHtml(html).trim().length > 0;
  });

  protected beginDescriptionEdit(): void {
    const project = this.project();
    if (!project) return;
    this.descriptionDraft.set(project.description ?? '');
    this.editingDescription.set(true);
  }

  protected cancelDescriptionEdit(): void {
    this.editingDescription.set(false);
    this.descriptionDraft.set('');
  }

  /** Save the current rich-text draft. Empty content (no visible
   *  characters after stripping HTML) is normalised to an empty string
   *  so the read-mode empty state surfaces cleanly. */
  protected async commitDescriptionEdit(): Promise<void> {
    const id = this.projectId();
    if (!id) {
      this.cancelDescriptionEdit();
      return;
    }
    const draft = this.descriptionDraft();
    const normalised = stripHtml(draft).trim().length > 0 ? draft : '';
    const project = this.project();
    if (project && normalised === (project.description ?? '')) {
      this.cancelDescriptionEdit();
      return;
    }
    await this.flowProjects.updateProject(id, { description: normalised });
    this.cancelDescriptionEdit();
  }

  // ── Archive / restore ──────────────────────────────

  protected async toggleArchive(): Promise<void> {
    const project = this.project();
    if (!project) return;
    if (project.archivedAt) {
      await this.flowProjects.restore(project._id);
    } else {
      // Not titled with the project's name: project names are masked in
      // session replay, and the shared confirm dialog renders outside this
      // panel with no way to carry the class.
      const ok = await this.confirmDialog.confirm({
        title: 'Archive this project?',
        message:
          'The project will drop out of pickers but stay readable in the Archived tab. Tagged sessions keep their reference.',
        confirmLabel: 'Archive project',
        cancelLabel: 'Cancel',
        danger: true,
      });
      if (!ok) return;
      await this.flowProjects.archive(project._id);
    }
  }

  // ── Meetings ───────────────────────────────────────

  /** Fetch the rich UCM projection for the project's attached
   *  meetings. Server returns subject + startTime + endTime +
   *  recording badge + isAutoAttached flag. */
  private async loadMeetings(projectId: string): Promise<void> {
    this.meetingsLoading.set(true);
    try {
      const rows = await this.flowProjects.fetchProjectMeetings(projectId);
      this.meetings.set(rows);
    } finally {
      this.meetingsLoading.set(false);
    }
  }

  /** Click-through on a meeting row — open it in THE meeting panel. The
   *  project panel closes so the meeting takes focus. */
  protected openMeeting(meetingId: string): void {
    this.shellState.closeProjectDetail();
    this.meetingPanel.openMeeting(meetingId);
  }

  protected async removeMeeting(meetingId: string): Promise<void> {
    const id = this.projectId();
    if (!id) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Remove this session from the project?',
      message:
        'It stays on your calendar — only the project tag is removed.',
      confirmLabel: 'Remove session',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    await this.flowProjects.removeMeeting(id, meetingId);
    // Refresh the local meetings list to reflect the removal.
    this.meetings.update(list => list.filter(m => m.meetingId !== meetingId));
  }

  /** Remove a recurring-series rule from the project. Pulls the rule
   *  + every meeting that came from that series. Refreshes the local
   *  meetings list so the panel reflects the bulk-removal. */
  protected async removeSeriesRule(seriesKey: string): Promise<void> {
    const id = this.projectId();
    if (!id) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Stop auto-including this recurring series?',
      message:
        'Every meeting in the project that came from this series will be removed. You can re-tag individual meetings manually.',
      confirmLabel: 'Stop auto-include',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    const success = await this.flowProjects.removeSeries(id, seriesKey);
    if (success) {
      // Reload the rich meetings list — back-fill removal flushed
      // multiple rows.
      void this.loadMeetings(id);
    }
  }

  /** Format an ISO timestamp as "Mon 5 May, 09:00" for the rich
   *  meeting rows. Mirrors the Flow card continuity surface. */
  protected formatMeetingTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', withClock({
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }));
  }

  // ── Knowledge integration ──────────────────────────

  /** Load the page list for the project's folder (or empty when
   *  unlinked). Hits the network — pages can change outside the
   *  project panel (recordings added via the Flow card, manual
   *  pages created via the Knowledge tree), so we don't cache. */
  private async loadKnowledgePages(projectId: string): Promise<void> {
    this.knowledgePagesLoading.set(true);
    try {
      const rows = await this.flowProjects.fetchKnowledgePages(projectId);
      this.knowledgePages.set(rows);
    } finally {
      this.knowledgePagesLoading.set(false);
    }
  }

  /** Open Knowledge focused on a specific page inside the project
   *  folder. Closes the project panel so the user lands cleanly on
   *  the Knowledge surface. */
  protected async openKnowledgePage(pageId: string): Promise<void> {
    const workspaceId = this.project()?.knowledgeWorkspaceId;
    if (!workspaceId) return;
    this.shellState.closeProjectDetail();
    await this.router.navigateByUrl(`/home/knowledge/${workspaceId}/${pageId}`);
  }

  /** Open Knowledge at the project's folder page (the "View all"
   *  link beneath the page list). */
  protected async openProjectFolder(): Promise<void> {
    const proj = this.project();
    if (!proj?.knowledgeWorkspaceId || !proj.knowledgeFolderPageId) return;
    this.shellState.closeProjectDetail();
    await this.router.navigateByUrl(
      `/home/knowledge/${proj.knowledgeWorkspaceId}/${proj.knowledgeFolderPageId}`,
    );
  }

  protected openWorkspacePicker(): void {
    this.workspacePickerOpen.set(true);
    this.workspacePickerQuery.set('');
  }

  protected closeWorkspacePicker(): void {
    this.workspacePickerOpen.set(false);
    this.workspacePickerQuery.set('');
  }

  /** Pick an existing workspace from the picker. Calls the service's
   *  optimistic link; on success surfaces the in-panel "✓ Linked"
   *  affordance, then re-loads the page list. */
  protected async commitLinkExisting(workspace: { _id: string; name: string }): Promise<void> {
    const id = this.projectId();
    if (!id) return;
    const updated = await this.flowProjects.linkKnowledgeWorkspace(
      id,
      workspace._id,
      workspace.name,
    );
    if (updated) {
      this.markKnowledgeJustLinked(workspace.name);
      void this.loadKnowledgePages(id);
    }
    this.closeWorkspacePicker();
  }

  /** Spin up a new workspace named after the project, then link it. */
  protected async commitCreateWorkspace(): Promise<void> {
    const id = this.projectId();
    const proj = this.project();
    if (!id || !proj) return;
    const updated = await this.flowProjects.createKnowledgeWorkspaceForProject(id);
    if (updated) {
      this.markKnowledgeJustLinked(updated.knowledgeWorkspaceName ?? proj.name);
      void this.loadKnowledgePages(id);
      void this.workspaceState.loadWorkspaces();
    }
    this.closeWorkspacePicker();
  }

  protected async unlinkWorkspace(): Promise<void> {
    const id = this.projectId();
    if (!id) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Unlink this Knowledge workspace?',
      message:
        'The pages stay in Knowledge — only the link to this project is broken. You can re-link any time.',
      confirmLabel: 'Unlink workspace',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    await this.flowProjects.unlinkKnowledgeWorkspace(id);
    this.knowledgePages.set([]);
  }

  private markKnowledgeJustLinked(name: string): void {
    if (this.knowledgeLinkedTimer) {
      clearTimeout(this.knowledgeLinkedTimer);
      this.knowledgeLinkedTimer = null;
    }
    this.knowledgeJustLinkedName.set(name);
    this.knowledgeLinkedTimer = setTimeout(() => {
      this.knowledgeJustLinkedName.set(null);
      this.knowledgeLinkedTimer = null;
    }, 1500);
  }

  // ── Helpers ────────────────────────────────────────

  protected memberCount(project: FlowProject): number {
    return project.members?.length ?? 0;
  }

  protected formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
}
